import formatMsg from '../../util/formatMsg';
import * as timeutil from '../../util/timeutil';

// Shared by the laptop and mobile profile views so the two cannot drift. Everything here
// is read-only: Illumine owns these records and the sync is their only writer, so there is
// no edit affordance to match.

// `key` is deliberately the locale key: AdditionalFieldCard labels each entry with
// formatMsg(item.key), so these rows can be handed to it as-is.
export interface ProfileRow {
    key: string;
    label: string;
    value: string;
}

// inFormat() falls back to "now" for a blank input, so an absent date has to be caught
// here rather than handed to it.
const asDate = (epoch: any): string =>
    typeof epoch === "number" ? timeutil.inFormat(epoch) : "-";

// Illumine stores an emergency contact's country code bare ("91") in its own column,
// while a Bookingo account stores the whole number already international ("+9198...").
// Composed here so both read the same on screen — a linked account's phone renders
// straight from phoneNumber, so this has to arrive in that same shape.
export const formatContactPhone = (countryCode: any, phone: any): string => {
    const number = String(phone ?? "").trim();
    if (!number) return "-";
    if (number.startsWith("+")) return number;

    const code = String(countryCode ?? "").replace(/\D/g, "");
    return code ? `+${code}${number.replace(/^0+/, "")}` : number;
};

const asText = (value: any): string =>
    value === null || value === undefined || value === "" ? "-" : String(value);

export const isIllumineOwned = (accountDetails: any): boolean =>
    Boolean(accountDetails?.migratedFromIllumine);

// The child's own details. Gender and date of birth sit on the user itself; the rest come
// from the Illumine profile row, which Bookingo has no columns of its own for.
//
// A blank row is still rendered, so the block does not reshuffle depending on which
// fields a given centre fills in.
export const getIllumineProfileRows = (accountDetails: any): ProfileRow[] => {
    const profile = accountDetails?.illumineProfile || {};

    return [
        { key: "gender", label: formatMsg("gender"), value: accountDetails?.gender ? formatMsg(accountDetails.gender) : "-" },
        { key: "dateOfBirth", label: formatMsg("dateOfBirth"), value: asDate(accountDetails?.birthDate) },
        { key: "admissionNumber", label: formatMsg("admissionNumber"), value: asText(profile.admissionNumber) },
        { key: "admissionDate", label: formatMsg("admissionDate"), value: asDate(profile.admissionDate) },
        { key: "registrationDate", label: formatMsg("registrationDate"), value: asDate(profile.registrationDate) },
        { key: "bloodGroup", label: formatMsg("bloodGroup"), value: asText(profile.bloodGroup) },
        { key: "medication", label: formatMsg("medication"), value: asText(profile.medication) },
        { key: "address", label: formatMsg("address"), value: asText(profile.address) },
    ];
};

export const getEmergencyContactRows = (contact: any): ProfileRow[] => [
    { key: "name", label: formatMsg("name"), value: asText(contact?.name) },
    { key: "relation", label: formatMsg("relation"), value: asText(contact?.relationship) },
    { key: "phone", label: formatMsg("phone"), value: formatContactPhone(contact?.countryCode, contact?.phone) },
    { key: "email", label: formatMsg("email"), value: asText(contact?.email) },
    { key: "address", label: formatMsg("address"), value: asText(contact?.address) },
];

// Who else may collect this child. Illumine keeps these on the student row rather than in
// their own node, but they reach Bookingo the same way and are just as read-only.
export const getAuthorizedPickupRows = (pickup: any): ProfileRow[] => [
    { key: "name", label: formatMsg("name"), value: asText(pickup?.name) },
    { key: "relation", label: formatMsg("relation"), value: asText(pickup?.relationship) },
    { key: "phone", label: formatMsg("phone"), value: formatContactPhone(pickup?.countryCode, pickup?.phoneNumber) },
    { key: "email", label: formatMsg("email"), value: asText(pickup?.email) },
    { key: "address", label: formatMsg("address"), value: asText(pickup?.address) },
];

export const getAllergyRows = (allergy: any): ProfileRow[] => [
    { key: "name", label: formatMsg("name"), value: asText(allergy?.allergyName) },
    { key: "category", label: formatMsg("category"), value: asText(allergy?.allergyCategory) },
    { key: "severity", label: formatMsg("severity"), value: asText(allergy?.severity) },
    { key: "symptoms", label: formatMsg("symptoms"), value: asText(allergy?.symptoms) },
    { key: "treatmentPlan", label: formatMsg("treatmentPlan"), value: asText(allergy?.treatmentPlan) },
    { key: "notes", label: formatMsg("notes"), value: asText(allergy?.notes) },
];
